// Report attachments — names, allowed types and content sniffing
// (docs/plans/2026-10-02-rapporten-ontwerp.md §3). Browser-safe: works on a
// plain Uint8Array, no Buffer.
//
// The stored content type always comes from the bytes (`sniffReportAsset`),
// never from the name. The name's extension must agree with what the bytes
// are (`reportAssetExtensionMatches`), so `evil.png` holding an SVG is
// refused instead of silently served as something else.

import { z } from 'zod'
import { REPORT_ASSET_NAME_MAX_LENGTH } from './limits.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const REPORT_ASSET_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/csv',
  'application/json',
] as const
export const ReportAssetContentTypeSchema = z.enum(REPORT_ASSET_CONTENT_TYPES)
export type ReportAssetContentType = z.infer<typeof ReportAssetContentTypeSchema>

/** `image`: can be embedded (`<img src="assets/…">`); `file`: shown as a download link outside the iframe. */
export type ReportAssetKind = 'image' | 'file'

export const REPORT_ASSET_TYPES: Readonly<Record<ReportAssetContentType, { kind: ReportAssetKind; extensions: readonly string[]; text: boolean }>> = {
  'image/png': { kind: 'image', extensions: ['png'], text: false },
  'image/jpeg': { kind: 'image', extensions: ['jpg', 'jpeg'], text: false },
  'image/gif': { kind: 'image', extensions: ['gif'], text: false },
  'image/webp': { kind: 'image', extensions: ['webp'], text: false },
  'image/svg+xml': { kind: 'image', extensions: ['svg'], text: true },
  'application/pdf': { kind: 'file', extensions: ['pdf'], text: false },
  'text/csv': { kind: 'file', extensions: ['csv'], text: true },
  'application/json': { kind: 'file', extensions: ['json'], text: true },
}

/** The type entry for a content type; own keys only, so `constructor`/`__proto__` are no types. */
function assetType(contentType: string): (typeof REPORT_ASSET_TYPES)[ReportAssetContentType] | undefined {
  return Object.prototype.hasOwnProperty.call(REPORT_ASSET_TYPES, contentType) ? REPORT_ASSET_TYPES[contentType as ReportAssetContentType] : undefined
}

export function reportAssetKind(contentType: string): ReportAssetKind {
  return assetType(contentType)?.kind ?? 'file'
}

/** Whether the type is text (SVG/CSV/JSON) — those get the secret check. */
export function isReportTextAsset(contentType: string): boolean {
  return assetType(contentType)?.text ?? false
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/** `[a-z0-9._-]+` with an extension, starting with a letter or digit, ≤ 100 characters. */
export const REPORT_ASSET_NAME_PATTERN = /^[a-z0-9][a-z0-9._-]*\.[a-z0-9]+$/

/** Returns an error message, or `null` when `name` is a valid asset name. */
export function reportAssetNameError(name: string): string | null {
  if (!name) return 'Asset name is empty'
  if (name.length > REPORT_ASSET_NAME_MAX_LENGTH) return `Asset name is longer than ${REPORT_ASSET_NAME_MAX_LENGTH} characters`
  if (!REPORT_ASSET_NAME_PATTERN.test(name)) {
    return `Asset name "${name}" must be lowercase letters, digits, '.', '_' or '-', start with a letter or digit, and have an extension (e.g. chart.png)`
  }
  if (name.includes('..')) return `Asset name "${name}" may not contain '..'`
  return null
}

export function isValidReportAssetName(name: string): boolean {
  return reportAssetNameError(name) === null
}

export const ReportAssetNameSchema = z.string().superRefine((name, ctx) => {
  const error = reportAssetNameError(name)
  if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error })
})

/**
 * A valid asset name from a file path or a loose name (`/tmp/Mijn Grafiek.PNG`
 * → `mijn-grafiek.png`): basename, lowercase, accents stripped, anything
 * outside `[a-z0-9._-]` → '-', capped at 100 characters (extension kept).
 * Returns `null` when nothing usable is left (e.g. no extension).
 */
export function deriveReportAssetName(pathOrName: string): string | null {
  const base = pathOrName.split(/[\\/]/).pop() ?? ''
  let name = base
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/\.{2,}/g, '.')
    .replace(/^[._-]+/, '')
  const dot = name.lastIndexOf('.')
  if (dot <= 0) return null
  let stem = name.slice(0, dot).replace(/[._-]+$/, '')
  const ext = name.slice(dot + 1).replace(/[^a-z0-9]/g, '')
  if (!stem || !ext) return null
  const max = REPORT_ASSET_NAME_MAX_LENGTH - ext.length - 1
  if (stem.length > max) stem = stem.slice(0, max).replace(/[._-]+$/, '')
  name = `${stem}.${ext}`
  return isValidReportAssetName(name) ? name : null
}

/** The lowercase extension of a name (`chart.PNG` → `png`), or '' without one. */
export function reportAssetExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

/** Whether `name`'s extension is one of `contentType`'s extensions. */
export function reportAssetExtensionMatches(name: string, contentType: string): boolean {
  const type = assetType(contentType)
  return !!type && type.extensions.includes(reportAssetExtension(name))
}

// ---------------------------------------------------------------------------
// Sniffing
// ---------------------------------------------------------------------------

function startsWith(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + signature.length) return false
  for (let i = 0; i < signature.length; i++) if (bytes[offset + i] !== signature[i]) return false
  return true
}

function ascii(text: string): number[] {
  return Array.from(text, (c) => c.charCodeAt(0))
}

/** Strict UTF-8 decode (BOM stripped); `null` when the bytes aren't valid UTF-8. */
function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
  } catch {
    return null
  }
}

function isXmlWhitespace(code: number): boolean {
  return code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d
}

function skipXmlWhitespace(text: string, from: number): number {
  let p = from
  while (p < text.length && isXmlWhitespace(text.charCodeAt(p))) p += 1
  return p
}

function startsWithIgnoreCase(text: string, prefix: string, at: number): boolean {
  return text.slice(at, at + prefix.length).toLowerCase() === prefix
}

/**
 * Whether the text starts like an SVG document: optional `<?xml …?>`, then
 * any number of comments and `<!DOCTYPE …>` (with an optional internal
 * subset), then an `<svg` root. A hand-written forward scan — each
 * character is looked at a bounded number of times, unlike the regex it
 * replaced (nested lazy groups backtracked exponentially on `<!--…-->`
 * runs).
 */
function hasSvgProlog(text: string): boolean {
  let p = skipXmlWhitespace(text, 0)
  if (text.startsWith('<?xml', p)) {
    const gt = text.indexOf('>', p)
    if (gt === -1 || text.charCodeAt(gt - 1) !== 0x3f /* ? */) return false
    p = skipXmlWhitespace(text, gt + 1)
  }
  for (;;) {
    if (text.startsWith('<!--', p)) {
      const close = text.indexOf('-->', p + 4)
      if (close === -1) return false
      p = skipXmlWhitespace(text, close + 3)
      continue
    }
    if (startsWithIgnoreCase(text, '<!doctype', p)) {
      let q = p + 9
      while (q < text.length && text.charCodeAt(q) !== 0x3e && text.charCodeAt(q) !== 0x5b /* [ */) q += 1
      if (q >= text.length) return false
      if (text.charCodeAt(q) === 0x5b) {
        const close = text.indexOf(']', q + 1)
        if (close === -1) return false
        q = skipXmlWhitespace(text, close + 1)
        if (text.charCodeAt(q) !== 0x3e) return false
      }
      p = skipXmlWhitespace(text, q + 1)
      continue
    }
    break
  }
  if (!startsWithIgnoreCase(text, '<svg', p)) return false
  const after = text.charCodeAt(p + 4)
  return isXmlWhitespace(after) || after === 0x3e /* > */ || after === 0x2f /* / */
}

/** Whether the (trimmed) text ends with `</svg>` or with a self-closing `<svg …/>`; looks at the end only. */
function hasSvgEnd(trimmed: string): boolean {
  if (!trimmed.endsWith('>')) return false
  // `</svg   >`
  const beforeGt = trimmed.slice(0, -1).trimEnd()
  if (beforeGt.slice(-5).toLowerCase() === '</svg') return true
  // `<svg … />` — the last `<svg` with no `>` between it and the final `/>`.
  if (!trimmed.endsWith('/>')) return false
  const previousGt = trimmed.lastIndexOf('>', trimmed.length - 3)
  const tail = trimmed.slice(previousGt + 1).toLowerCase()
  return tail.includes('<svg')
}

function looksLikeSvg(text: string): boolean {
  return hasSvgProlog(text) && hasSvgEnd(text.trimEnd())
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false
  try {
    JSON.parse(trimmed)
    return true
  } catch {
    return false
  }
}

/** Non-empty text with no control characters other than tab/CR/LF. */
function looksLikeCsv(text: string): boolean {
  if (!text.trim()) return false
  // eslint-disable-next-line no-control-regex
  return !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(text)
}

/**
 * The content type of an attachment, from its bytes: magic bytes for
 * PNG/JPEG/GIF/WebP/PDF; for text, valid UTF-8 plus a parse check — SVG
 * (an `<svg>` root), JSON (`JSON.parse` of an object/array), CSV (plain text
 * without control characters). `nameHint` only decides between JSON and CSV
 * for text that would pass both checks; it never makes bytes into a type
 * they aren't. `null` = not an allowed type.
 */
export function sniffReportAsset(bytes: Uint8Array, nameHint?: string): ReportAssetContentType | null {
  if (bytes.length === 0) return null
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (startsWith(bytes, ascii('GIF87a')) || startsWith(bytes, ascii('GIF89a'))) return 'image/gif'
  if (startsWith(bytes, ascii('RIFF')) && startsWith(bytes, ascii('WEBP'), 8)) return 'image/webp'
  if (startsWith(bytes, ascii('%PDF-'))) return 'application/pdf'

  const text = decodeUtf8(bytes)
  if (text === null) return null
  if (looksLikeSvg(text)) return 'image/svg+xml'
  const ext = nameHint ? reportAssetExtension(nameHint) : ''
  if (ext !== 'csv' && looksLikeJson(text)) return 'application/json'
  if (looksLikeCsv(text)) return 'text/csv'
  return null
}

/** UTF-8 text of a text asset (for the secret check); `null` for binary types or invalid UTF-8. */
export function reportAssetText(bytes: Uint8Array, contentType: string): string | null {
  return isReportTextAsset(contentType) ? decodeUtf8(bytes) : null
}
